import React, { useEffect, useState } from "react";
import {
    Box, Button, Flex, Text, Icon, Divider, Switch, useColorModeValue,
} from "@chakra-ui/react";
import { MessageCircle, FileText } from "lucide-react";
import Card from "components/card/Card.js";
import { QuickAlert } from "theme/components/alerts";
import { userController } from "lib/user";
import BeatLoader from "react-spinners/BeatLoader";

function AppSettingsGeneral() {
    const { setAlert, clearAlert, showAlert } = QuickAlert();
    const { getSingleRecord, saveRecord } = userController();
    const headerBorderColor = useColorModeValue("#E6ECFA", "rgba(135, 140, 189, 0.3)");

    const [generalLoading, setGeneralLoading]   = useState(true);
    const [savingGeneral, setSavingGeneral]     = useState(false);
    const [whatsappEnabled, setWhatsappEnabled] = useState(true);
    const [documentsEnabled, setDocumentsEnabled] = useState(true);

    useEffect(() => {
        loadGeneralSettings();
    }, []);

    const loadGeneralSettings = () => {
        setGeneralLoading(true);
        getSingleRecord("/user/app-settings/general")
            .then((res) => {
                setWhatsappEnabled(res.data.whatsapp_chat_enabled);
                setDocumentsEnabled(res.data.documents_enabled);
                setGeneralLoading(false);
            })
            .catch(() => setGeneralLoading(false));
    };

    const saveGeneralSettings = () => {
        setSavingGeneral(true);
        clearAlert();
        saveRecord("/user/app-settings/general", { whatsapp_chat_enabled: whatsappEnabled, documents_enabled: documentsEnabled })
            .then((res) => {
                const existing = JSON.parse(localStorage.getItem("app_settings") || "{}");
                localStorage.setItem("app_settings", JSON.stringify({
                    ...existing,
                    whatsapp_chat_enabled: whatsappEnabled,
                    documents_enabled: documentsEnabled,
                    whatsapp_support_visible: res.data.whatsapp_support_visible,
                }));
                setAlert("success", res.data.message);
                setSavingGeneral(false);
            })
            .catch((error) => {
                setAlert("error", error.response.data.errors);
                setSavingGeneral(false);
            });
    };

    return (
        <Box>
            <Card mb="20px" p="0">
                <Flex justify="space-between" align="center" pt="20px" pb="16px" px="24px" borderBottom="1px solid" borderColor={headerBorderColor}>
                    <Text color="secondaryGray.900" fontSize="22px" fontWeight="700" lineHeight="100%">
                        Impostazioni Generali
                    </Text>
                </Flex>
                <Box px="24px" pt="20px" pb="24px">
                {showAlert()}

                {generalLoading ? (
                    <Flex justify="center" py="20px">
                        <BeatLoader size={10} color="#422CFA" />
                    </Flex>
                ) : (
                    <>
                        {/* AI WhatsApp Chat row */}
                        <Flex align="center" justify="space-between" flexWrap="wrap" gap="16px">
                            <Flex align="center" gap="14px">
                                <Flex
                                    w="48px" h="48px" borderRadius="12px"
                                    bg="oklch(97% .014 254.604)" align="center" justify="center" flexShrink={0}
                                >
                                    <Icon as={MessageCircle} boxSize="24px" color="oklch(48.8% .243 264.376)" />
                                </Flex>
                                <Box>
                                    <Text fontWeight="semibold" fontSize="md" mb="2px">Chat WhatsApp AI</Text>
                                    <Text fontSize="xs" color="gray.500">
                                        {whatsappEnabled
                                            ? "L'assistente AI è attivo e risponde ai messaggi degli ospiti."
                                            : "L'assistente AI è disattivato. Gli ospiti ricevono un messaggio automatico di non disponibilità — i numeri elencati in Agente AI → Numeri di Test raggiungono comunque l'assistente."}
                                    </Text>
                                </Box>
                            </Flex>
                            <Switch
                                colorScheme="brand"
                                size="lg"
                                isChecked={whatsappEnabled}
                                onChange={(e) => setWhatsappEnabled(e.target.checked)}
                            />
                        </Flex>

                        <Divider my="24px" />

                        {/* Guest Documents row */}
                        <Flex align="center" justify="space-between" flexWrap="wrap" gap="16px">
                            <Flex align="center" gap="14px">
                                <Flex
                                    w="48px" h="48px" borderRadius="12px"
                                    bg="oklch(97% .014 254.604)" align="center" justify="center" flexShrink={0}
                                >
                                    <Icon as={FileText} boxSize="24px" color="oklch(48.8% .243 264.376)" />
                                </Flex>
                                <Box>
                                    <Text fontWeight="semibold" fontSize="md" mb="2px">Documenti Ospiti</Text>
                                    <Text fontSize="xs" color="gray.500">
                                        {documentsEnabled
                                            ? "La scheda Documenti è visibile nella bacheca proprietà."
                                            : "La scheda Documenti è nascosta nella bacheca proprietà."}
                                    </Text>
                                </Box>
                            </Flex>
                            <Switch
                                colorScheme="brand"
                                size="lg"
                                isChecked={documentsEnabled}
                                onChange={(e) => setDocumentsEnabled(e.target.checked)}
                            />
                        </Flex>
                    </>
                )}
                </Box>
                {!generalLoading && (
                    <Flex align="center" pt="16px" pb="16px" px="24px" borderTop="1px solid" borderColor={headerBorderColor}>
                        <Button
                            variant="brand"
                            minWidth="150px"
                            isLoading={savingGeneral}
                            onClick={saveGeneralSettings}
                        >
                            Aggiorna
                        </Button>
                    </Flex>
                )}
            </Card>
        </Box>
    );
}

export default AppSettingsGeneral;
